// Where an internal notice's reply goes (2026-11-04, Jenn; server
// services/notice-reply-target.js, GET /api/ai/conversations/:id/notice-reply-target).
// An internal notice (1A_INTERNAL_EMAIL / AM_INTERNAL_EMAIL) about an eBay
// order is answered to the order's buyer on eBay: the panel says so, sends
// no email To, and the server sends an eBay member message. A Website or
// Walmart order (target null) keeps today's email to the order's customer.
//
// target: { kind: 'ebay_member', buyerId, itemId } | { kind: 'email', to }
//       | { missing, reason } | null

import { isInternalAddress } from './noticeReply.js'

export const INTERNAL_NOTICE_ACCOUNTS = Object.freeze(['1A_INTERNAL_EMAIL', 'AM_INTERNAL_EMAIL'])
export const NO_BUYER_OR_EMAIL_REASON = 'No buyer or customer email on this order'

export function isInternalNoticeAccount(account) {
  return INTERNAL_NOTICE_ACCOUNTS.includes(account)
}

// The reply goes to the eBay buyer: a reply (not a new outbound email) on a
// target with a buyer.
export function noticeRepliesOnEbay(target, replyMode) {
  return target?.kind === 'ebay_member' && Boolean(target.buyerId) && replyMode !== 'new'
}

// The line at the top of the reply: "Reply goes to the buyer on eBay: <buyer>".
export function noticeEbayLine(target) {
  return target?.kind === 'ebay_member' && target.buyerId ? `Reply goes to the buyer on eBay: ${target.buyerId}` : null
}

// Why Send is stopped (no address), or null: no buyer and no customer email,
// and no customer address in the To box (an internal address, such as the
// notice's own sender, is no customer).
export function noticeTargetBlockReason(target, draftTo) {
  if (!target?.missing) return null
  const to = String(draftTo ?? '').trim()
  return to && !isInternalAddress(to) ? null : (target.reason || NO_BUYER_OR_EMAIL_REASON)
}

// The eBay signature the reply box uses when the reply goes to the buyer on
// eBay (2026-11-04, Jenn): the eBay signature template by the order's company,
// from the server (target.signatureTemplate), else null (the box keeps its
// usual signature).
export function noticeEbaySignatureOf(target) {
  return target?.kind === 'ebay_member' && target.buyerId && target.signatureTemplate ? target.signatureTemplate : null
}

// The box with the email signature swapped for the eBay one: the email
// signature replaced where it is in the box, a blank box seeded with the eBay
// signature; any other text left as it is.
// fromSignature: one form of the email signature or several (as the box
// resolves it, as a loaded draft resolves it, as stored); the first found wins.
export function swapNoticeSignature(draft, fromSignature, toSignature) {
  const text = String(draft ?? '')
  if (!toSignature) return text
  const froms = (Array.isArray(fromSignature) ? fromSignature : [fromSignature]).filter((f) => typeof f === 'string' && f.trim())
  const found = froms.find((f) => text.includes(f))
  if (found) return text.replace(found, toSignature)
  const bare = text.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()
  return bare ? text : `<p></p>${toSignature}`
}
